import React, { useEffect, useRef } from 'react';

/**
 * Gravity particle field.
 * - Dots drift and bounce off the container edges and off the cards in `obstacles`.
 * - The cursor pulls dots within RADIUS. Each captured dot makes the pull a little
 *   stronger (the "snowball"), eased and capped so a big swarm stays stable.
 * - Collect every dot and `onComplete` fires once.
 * - prefers-reduced-motion: dots don't drift (the cursor still works).
 *
 * Design notes (what used to glitch):
 * - Dots never step past their resting distance from the cursor, so a swarm can't
 *   oscillate back and forth across it.
 * - The swarm's strength counts dots within CAPTURE (not 10px) and eases toward
 *   its target, so moving the mouse doesn't make the swarm pulse and burst.
 * - Dots being pulled slide underneath the cards (the canvas is behind them)
 *   instead of pinning against an edge; free dots bounce off the side they hit.
 * - The cursor is stored in screen coordinates and converted every frame, so
 *   scrolling doesn't leave the swarm chasing a stale point.
 * - The canvas is drawn at devicePixelRatio, so dots stay crisp on scaled displays.
 */
const RADIUS = 150; // pull range, px
const CAPTURE = 24; // a dot this close counts toward the swarm
const COLLECT = 12; // a dot this close counts as "collected" for the easter egg
const MAX_BOOST = 60; // cap on the swarm's extra strength
const MAX_STEP = 18; // px per frame, so dots never teleport
const DOT = 'rgba(42, 85, 201, 0.8)';
const HELD = '#141A26';

export default function FieldBackground({ containerRef, obstacles = [], onComplete }) {
  const canvasRef = useRef(null);
  const doneRef = useRef(false);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    const container = containerRef?.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const pointer = { x: null, y: null }; // client (screen) coordinates
    let W = 0;
    let H = 0;
    let particles = [];
    let boost = 0; // eased swarm strength
    let raf;

    const boxes = (origin) =>
      obstacles
        .map((r) => r?.current)
        .filter(Boolean)
        .map((el) => {
          const b = el.getBoundingClientRect();
          return { x: b.left - origin.left - 6, y: b.top - origin.top - 6, w: b.width + 12, h: b.height + 12 };
        });

    const seed = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = container.offsetWidth;
      H = container.offsetHeight;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const bx = boxes(canvas.getBoundingClientRect());
      const blocked = (px, py) => bx.some((b) => px > b.x && px < b.x + b.w && py > b.y && py < b.y + b.h);
      const n = Math.ceil((W * H) / 1e4);
      particles = [];
      for (let i = 0; i < n; i += 1) {
        let x;
        let y;
        let tries = 0;
        do {
          x = Math.random() * W;
          y = Math.random() * H;
          tries += 1;
        } while (tries < 20 && blocked(x, y));
        particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 2,
          vy: (Math.random() - 0.5) * 2,
          rest: 2 + Math.random() * 8, // how close it settles, so a swarm is a small cloud
        });
      }
      boost = 0;
    };

    const step = () => {
      const origin = canvas.getBoundingClientRect();
      const bx = boxes(origin);
      const inside = (x, y) => bx.find((r) => x + 2 > r.x && x - 2 < r.x + r.w && y + 2 > r.y && y - 2 < r.y + r.h);
      const mx = pointer.x === null ? -1e4 : pointer.x - origin.left;
      const my = pointer.y === null ? -1e4 : pointer.y - origin.top;
      const strength = 10 + boost;
      let captured = 0;
      let collected = 0;

      ctx.clearRect(0, 0, W, H);
      ctx.lineCap = 'round';

      for (const p of particles) {
        const ox = p.x;
        const oy = p.y;
        if (!reduce) {
          p.x += p.vx;
          p.y += p.vy;
        }
        if (p.x <= 0 || p.x >= W) p.vx *= -1;
        if (p.y <= 0 || p.y >= H) p.vy *= -1;

        const dx = mx - p.x;
        const dy = my - p.y;
        const d = Math.sqrt(dx * dx + dy * dy);

        if (d < RADIUS) {
          const near = d < CAPTURE;
          const pull = (0.1 * strength * (RADIUS - d)) / RADIUS * (near ? 1.1 : 2);
          const s = Math.min(pull, Math.max(0, d - p.rest), MAX_STEP);
          if (d > 0) {
            p.x += (dx / d) * s;
            p.y += (dy / d) * s;
          }
          const a = (RADIUS - d) / RADIUS;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(42, 85, 201, ${0.5 * a})`;
          ctx.lineWidth = 2 * a;
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(mx, my);
          ctx.stroke();
        } else {
          // free dots bounce off the side of the card they came through
          const b = inside(p.x, p.y);
          if (b && !inside(ox, oy)) {
            if (ox + 2 <= b.x || ox - 2 >= b.x + b.w) {
              p.vx *= -1;
              p.x = ox;
            } else {
              p.vy *= -1;
              p.y = oy;
            }
          }
        }

        p.x = Math.max(0, Math.min(p.x, W));
        p.y = Math.max(0, Math.min(p.y, H));

        const after = Math.hypot(mx - p.x, my - p.y);
        if (after < CAPTURE) captured += 1;
        if (after < COLLECT) collected += 1;
        ctx.beginPath();
        ctx.fillStyle = after < CAPTURE ? HELD : DOT;
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // ease the swarm strength toward its target so it never spikes or collapses in one frame
      boost += (Math.min(captured, MAX_BOOST) - boost) * 0.08;

      if (!doneRef.current && particles.length && collected === particles.length) {
        doneRef.current = true;
        completeRef.current?.();
      }
      raf = requestAnimationFrame(step);
    };

    const onMove = (e) => {
      pointer.x = e.clientX;
      pointer.y = e.clientY;
    };
    const onLeave = () => {
      pointer.x = null;
      pointer.y = null;
    };

    seed();
    raf = requestAnimationFrame(step);
    const ro = new ResizeObserver(() => {
      if (W !== container.offsetWidth || H !== container.offsetHeight) seed();
    });
    ro.observe(container);
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
    // obstacles are refs; their .current is read every frame
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);

  return <canvas ref={canvasRef} className="qs-canvas" aria-hidden="true" />;
}
